import React from 'react';
import styled, { keyframes } from 'styled-components';
import { FiX } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';

// Version comes from .env (injected by CI/CD in production)
const APP_VERSION = process.env.REACT_APP_VERSION || '0.0.0';

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const slideUp = keyframes`
  from {
    opacity: 0;
    transform: translate(-50%, -50%) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1);
  }
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  z-index: 10000;
  animation: ${fadeIn} 0.2s ease-out;
`;

const Modal = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: ${(p) => p.theme.colors.surface};
  border-radius: 16px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  z-index: 10001;
  width: 90%;
  max-width: 360px;
  animation: ${slideUp} 0.3s ease-out;
  overflow: hidden;
`;

const Header = styled.div`
  background: ${(p) => p.theme.colors.primaryDark};
  padding: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 12px;
  right: 12px;
  background: rgba(255, 255, 255, 0.2);
  border: none;
  border-radius: 8px;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: white;
  transition: background 0.2s;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
  }
`;

const Logo = styled.img`
  width: 64px;
  height: 64px;
  border-radius: 12px;
`;

const Content = styled.div`
  padding: 24px;
  text-align: center;
`;

const AppName = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  color: ${(p) => p.theme.colors.text};
  margin: 0 0 4px 0;
`;

const Version = styled.p`
  font-size: 1rem;
  color: ${(p) => p.theme.colors.primary};
  font-weight: 500;
  margin: 0 0 16px 0;
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 10px 0;
  border-bottom: 1px solid ${(p) => p.theme.colors.backgroundLight};
  font-size: 0.875rem;

  &:last-child {
    border-bottom: none;
  }
`;

const InfoLabel = styled.span`
  color: ${(p) => p.theme.colors.textLight};
`;

const InfoValue = styled.span`
  color: ${(p) => p.theme.colors.text};
  font-weight: 500;
`;

const CloseFooterButton = styled.button`
  width: 100%;
  padding: 12px 20px;
  border-radius: 10px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
  background: ${(p) => p.theme.colors.backgroundLight};
  color: ${(p) => p.theme.colors.text};
  margin-top: 16px;

  &:hover {
    background: ${(p) => p.theme.colors.background};
  }
`;

interface AboutModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const AboutModal: React.FC<AboutModalProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation();

  if (!isOpen) return null;

  const currentYear = new Date().getFullYear();

  return (
    <>
      <Overlay onClick={onClose} />
      <Modal>
        <Header>
          <CloseButton onClick={onClose}>
            <FiX size={18} />
          </CloseButton>
          <Logo src="/icon-192x192.png" alt="Smartsell" />
        </Header>
        <Content>
          <AppName>Smartsell Admin</AppName>
          <Version>v{APP_VERSION}</Version>

          <InfoRow>
            <InfoLabel>{t('version')}</InfoLabel>
            <InfoValue>{APP_VERSION}</InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>{t('year')}</InfoLabel>
            <InfoValue>{currentYear}</InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>{t('platform')}</InfoLabel>
            <InfoValue>Web</InfoValue>
          </InfoRow>

          <CloseFooterButton onClick={onClose}>
            {t('close')}
          </CloseFooterButton>
        </Content>
      </Modal>
    </>
  );
};

export default AboutModal;
